import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../api';

/**
 * OnboardingChecklist
 *
 * Creator-only prep strip that lives at the top of `/create-clan`. Two-track
 * design based on the creator's launch style:
 *
 *   ⚡ Launch Now (casual)   → 1 row: "Design your Clan"
 *   📅 Schedule Launch (recommended, unlocks more prep):
 *       1. Verify your socials
 *       2. Grab your referral link
 *       3. Share it with your audience   (X button + Share popover)
 *       4. Design your Clan (final)
 *
 * If the creator hasn't picked a launch style yet, we show a chooser card
 * first with copy explaining why scheduling wins more often. The choice
 * persists via `POST /api/onboarding/launch-intent`.
 *
 * Hides itself for admins, users who already have a Clan, and once
 * `all_done` / `dismissed` are true.
 */

// Share targets in the "Share →" popover. Kept alongside the standalone
// "Share on X" button, per user feedback ("X option stays as it is").
const SHARE_TARGETS = [
  {
    key: 'facebook',
    label: 'Facebook',
    emoji: '📘',
    build: (url, _text) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    emoji: '💬',
    build: (url, text) => `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    key: 'telegram',
    label: 'Telegram',
    emoji: '✈️',
    build: (url, text) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    emoji: '💼',
    build: (url, _text) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
];

export default function OnboardingChecklist() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedFlash, setCopiedFlash] = useState(false);
  const [shareCopiedFlash, setShareCopiedFlash] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/onboarding/checklist');
      setState(data);
    } catch (_) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    if (!user || user === false) return;
    refresh();
  }, [user, refresh]);

  // Close the share popover on outside click / Escape
  useEffect(() => {
    if (!showShareMenu) return;
    const onDoc = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setShowShareMenu(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowShareMenu(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [showShareMenu]);

  const referralLink = (() => {
    const base = window.location.origin;
    return `${base}/onboarding?code=${user?.referral_code || ''}`;
  })();
  const shareText = t('checklist.shareText');

  const mark = async (step) => {
    setBusy(true);
    try {
      const { data } = await api.post('/onboarding/mark', { step });
      setState(data);
    } catch (_) { /* non-fatal */ }
    finally { setBusy(false); }
  };

  const setLaunchIntent = async (intent) => {
    setBusy(true);
    try {
      const { data } = await api.post('/onboarding/launch-intent', { intent });
      setState(data);
    } catch (_) { /* non-fatal */ }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopiedFlash(true);
      setTimeout(() => setCopiedFlash(false), 1600);
      await mark('referral_copied');
    } catch (_) { /* non-fatal */ }
  };

  const shareOnX = async () => {
    const text = encodeURIComponent(shareText);
    const url = encodeURIComponent(referralLink);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'noopener,noreferrer');
    await mark('referral_shared');
  };

  const shareOnPlatform = async (target) => {
    const href = target.build(referralLink, shareText);
    window.open(href, '_blank', 'noopener,noreferrer');
    setShowShareMenu(false);
    await mark('referral_shared');
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setShareCopiedFlash(true);
      setTimeout(() => { setShareCopiedFlash(false); setShowShareMenu(false); }, 1400);
      await mark('referral_shared');
    } catch (_) { /* non-fatal */ }
  };

  const openShare = async () => {
    // Native OS share sheet on mobile if available; otherwise open our popover.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: 'Clanster', text: shareText, url: referralLink });
        await mark('referral_shared');
        return;
      } catch (_) { /* user cancelled — fall through to popover */ }
    }
    setShowShareMenu((v) => !v);
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post('/onboarding/dismiss');
      setState((s) => ({ ...s, dismissed: true }));
    } catch (_) { /* non-fatal */ }
    finally { setBusy(false); }
  };

  if (!state) return null;
  if (state.dismissed) return null;
  if (state.all_done) return null;
  if (state.role === 'admin') return null;

  // Only render on the Create Clan page (defence-in-depth in case the
  // component ever gets mounted elsewhere by accident).
  if (location.pathname !== '/create-clan') return null;

  // ── Completion state: Clan is live → celebratory success card ────────
  // Replaces the previous "unmount when has_clan" behaviour so creators get
  // visible closure + a jumping-off point to their next moves.
  if (state.steps.has_clan) {
    const clanUrl = `${window.location.origin}/api/share/creator/${user?.handle || ''}`;
    const clanShareText = t('checklist.clanLiveShareText');
    const shareClanOnX = () => {
      const t2 = encodeURIComponent(clanShareText);
      const u = encodeURIComponent(clanUrl);
      window.open(`https://twitter.com/intent/tweet?text=${t2}&url=${u}`, '_blank', 'noopener,noreferrer');
    };
    return (
      <div className="onb-strip onb-strip-complete" data-testid="onboarding-checklist-complete">
        <div className="onb-head">
          <div className="onb-title-wrap">
            <span className="onb-flag" aria-hidden="true">🎉</span>
            <div>
              <div className="onb-title" data-testid="onb-title">{t('checklist.completeTitle')}</div>
              <div className="onb-sub">
                {t('checklist.completeSub')}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="onb-close"
            onClick={dismiss}
            disabled={busy}
            aria-label={t('checklist.dismissAria')}
            title={t('checklist.dismissAria')}
            data-testid="onb-dismiss-btn"
          >
            ×
          </button>
        </div>

        <div className="onb-complete-actions">
          <button
            type="button"
            className="onb-complete-btn"
            onClick={shareClanOnX}
            data-testid="onb-complete-share"
          >
            <span className="onb-complete-emoji" aria-hidden="true">🚀</span>
            <span className="onb-complete-label">{t('checklist.shareClanLabel')}</span>
            <span className="onb-complete-sub">{t('checklist.shareClanSub')}</span>
          </button>
          <button
            type="button"
            className="onb-complete-btn"
            onClick={copyLink}
            data-testid="onb-complete-invite"
          >
            <span className="onb-complete-emoji" aria-hidden="true">👥</span>
            <span className="onb-complete-label">
              {copiedFlash ? t('checklist.referralCopiedFlash') : t('checklist.inviteFansLabel')}
            </span>
            <span className="onb-complete-sub">{t('checklist.inviteFansSub')}</span>
          </button>
          <button
            type="button"
            className="onb-complete-btn"
            onClick={() => navigate(`/creator/${user?.handle || ''}`)}
            data-testid="onb-complete-open"
          >
            <span className="onb-complete-emoji" aria-hidden="true">🎯</span>
            <span className="onb-complete-label">{t('checklist.openClanLabel')}</span>
            <span className="onb-complete-sub">{t('checklist.openClanSub')}</span>
          </button>
        </div>
      </div>
    );
  }

  const intent = state.launch_intent; // 'now' | 'scheduled' | null

  // ── Step 1: no intent picked yet → show the launch-style chooser ─────
  if (!intent) {
    return (
      <div className="onb-strip onb-strip-chooser" data-testid="onboarding-checklist">
        <div className="onb-head">
          <div className="onb-title-wrap">
            <span className="onb-flag" aria-hidden="true">🚀</span>
            <div>
              <div className="onb-title" data-testid="onb-title">{t('checklist.chooserTitle')}</div>
              <div className="onb-sub">{t('checklist.chooserSub')}</div>
            </div>
          </div>
          <button
            type="button"
            className="onb-close"
            onClick={dismiss}
            disabled={busy}
            aria-label={t('checklist.dismissAria')}
            title={t('checklist.dismissAria')}
            data-testid="onb-dismiss-btn"
          >
            ×
          </button>
        </div>

        <div className="onb-intent-grid">
          <button
            type="button"
            className="onb-intent-card"
            onClick={() => setLaunchIntent('now')}
            disabled={busy}
            data-testid="onb-intent-now"
          >
            <div className="onb-intent-emoji" aria-hidden="true">⚡</div>
            <div className="onb-intent-title">{t('checklist.intentNowTitle')}</div>
            <div className="onb-intent-sub">{t('checklist.intentNowSub')}</div>
          </button>
          <button
            type="button"
            className="onb-intent-card onb-intent-recommended"
            onClick={() => setLaunchIntent('scheduled')}
            disabled={busy}
            data-testid="onb-intent-scheduled"
          >
            <div className="onb-intent-badge">{t('checklist.intentBadgeRecommended')}</div>
            <div className="onb-intent-emoji" aria-hidden="true">📅</div>
            <div className="onb-intent-title">{t('checklist.intentScheduledTitle')}</div>
            <div className="onb-intent-sub">{t('checklist.intentScheduledSub')}</div>
          </button>
        </div>

        <div className="onb-intent-nudge" data-testid="onb-intent-nudge">
          💡 <b>{t('checklist.intentNudgeBold')}</b> {t('checklist.intentNudgeBody')}
        </div>
      </div>
    );
  }

  // ── Step 2: intent picked → conditional row list ─────────────────────
  const { steps } = state;

  const designRow = {
    key: 'has_clan',
    done: steps.has_clan,
    title: t('checklist.rowDesignTitle'),
    subtitle: t('checklist.rowDesignSub'),
    hereBadge: true, // creator is literally on this page
  };

  const rows = intent === 'now' ? [designRow] : [
    {
      key: 'verified',
      done: steps.verified,
      title: t('checklist.rowVerifyTitle'),
      subtitle: t('checklist.rowVerifySub'),
      action: { label: t('checklist.rowVerifyAction'), onClick: () => navigate('/verify') },
    },
    {
      key: 'referral_copied',
      done: steps.referral_copied,
      title: t('checklist.rowReferralTitle'),
      subtitle: t('checklist.rowReferralSub'),
      action: {
        label: copiedFlash ? t('checklist.rowReferralCopied') : t('checklist.rowReferralAction'),
        onClick: copyLink,
      },
    },
    {
      key: 'referral_shared',
      done: steps.referral_shared,
      title: t('checklist.rowShareTitle'),
      subtitle: t('checklist.rowShareSub'),
      customAction: true, // rendered manually below (X + Share sheet)
    },
    designRow,
  ];

  const doneCount = rows.filter((r) => r.done).length;
  const total = rows.length;
  const pct = Math.round((doneCount / total) * 100);

  const intentLabel = intent === 'now'
    ? t('checklist.intentLabelNow')
    : t('checklist.intentLabelScheduled');

  return (
    <div className="onb-strip" data-testid="onboarding-checklist">
      <div className="onb-head">
        <div className="onb-title-wrap">
          <span className="onb-flag" aria-hidden="true">🏁</span>
          <div>
            <div className="onb-title" data-testid="onb-title">{t('checklist.getStartedTitle')}</div>
            <div className="onb-sub" data-testid="onb-progress">
              {t('checklist.progressLine', { done: doneCount, total })} · {intentLabel}
              {' · '}
              <button
                type="button"
                className="onb-intent-change"
                onClick={() => setLaunchIntent(intent === 'now' ? 'scheduled' : 'now')}
                disabled={busy}
                data-testid="onb-intent-change"
              >
                {t('checklist.change')}
              </button>
            </div>
          </div>
        </div>
        <div className="onb-head-right">
          <div className="onb-bar" title={`${pct}%`} aria-label={`${pct} percent complete`}>
            <div className="onb-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <button
            type="button"
            className="onb-close"
            onClick={dismiss}
            disabled={busy}
            aria-label={t('checklist.dismissAria')}
            title={t('checklist.dismissAria')}
            data-testid="onb-dismiss-btn"
          >
            ×
          </button>
        </div>
      </div>

      <div className="onb-rows">
        {rows.map((r) => (
          <div
            key={r.key}
            className={`onb-row ${r.done ? 'is-done' : ''}`}
            data-testid={`onb-row-${r.key}`}
          >
            <div className={`onb-check ${r.done ? 'is-done' : ''}`} aria-hidden="true">
              {r.done ? '✓' : ''}
            </div>
            <div className="onb-row-text">
              <div className="onb-row-title">{r.title}</div>
              <div className="onb-row-sub">{r.subtitle}</div>
            </div>

            {/* Row-3 (share) gets a dual-button set: standalone X + Share sheet */}
            {r.customAction && !r.done && (
              <div className="onb-share-wrap" ref={shareMenuRef}>
                <button
                  type="button"
                  className="onb-row-btn"
                  onClick={shareOnX}
                  disabled={busy}
                  data-testid="onb-action-share-x"
                >
                  {t('checklist.shareOnXBtn')}
                </button>
                <button
                  type="button"
                  className="onb-row-btn onb-row-btn-alt"
                  onClick={openShare}
                  disabled={busy}
                  data-testid="onb-action-share-more"
                  aria-expanded={showShareMenu}
                  aria-haspopup="menu"
                >
                  {t('checklist.shareMoreBtn')}
                </button>
                {showShareMenu && (
                  <div className="onb-share-menu" role="menu" data-testid="onb-share-menu">
                    {SHARE_TARGETS.map((tgt) => (
                      <button
                        key={tgt.key}
                        type="button"
                        className="onb-share-item"
                        onClick={() => shareOnPlatform(tgt)}
                        role="menuitem"
                        data-testid={`onb-share-${tgt.key}`}
                      >
                        <span className="onb-share-emoji" aria-hidden="true">{tgt.emoji}</span>
                        <span>{tgt.label}</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="onb-share-item"
                      onClick={copyShareLink}
                      role="menuitem"
                      data-testid="onb-share-copy"
                    >
                      <span className="onb-share-emoji" aria-hidden="true">📋</span>
                      <span>{shareCopiedFlash ? t('checklist.copiedFlash') : t('checklist.copyLinkLabel')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Design your Clan row — creator is on this page, so no button. */}
            {r.hereBadge && !r.done && (
              <span className="onb-here-badge" data-testid="onb-here-badge" aria-hidden="true">
                {t('checklist.hereBadge')}
              </span>
            )}

            {/* Regular action button (verify / copy referral) */}
            {r.action && !r.done && (
              <button
                type="button"
                className="onb-row-btn"
                onClick={r.action.onClick}
                disabled={busy}
                data-testid={`onb-action-${r.key}`}
              >
                {r.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
